import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { VddDetailsListStore } from '@store';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import {
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
  SetNewValueObject,
} from '@vi-iot/client';
import {
  DataKeyValues,
  LongnameValues,
  SemanticValues,
  TranslationIdValues,
  TypeValues,
} from '../../../diagnostics-constants';
import { DiagnosticsHelpers } from '../../../diagnostics-helpers';
import { DiagnosticsFormGroupService } from '../../diagnostics-form-group.service';
import { VddDetailsListHelper } from '../../../../../../../@store/vdd-details-list/vdd-details-list.helper';
import { DiagnosticsWebsocketService } from '../../../diagnostics-websocket.service';
import {
  createDiagnosticsTextComponent,
  createVddBooleanChoiceComponent,
  createDiagnosticsVddDateComponent,
  createDiagnosticsVddEnumComponent,
  createDiagnosticsVddIpAddressComponent,
  createDiagnosticsVddMacAddressComponent,
  createDiagnosticsVddNumberComponent,
  createVddStartStopComponent,
  createDiagnosticsVinComponent,
  createSensorComponent,
} from './diagnostics-dynamic-structure-helper';

@Component({
  selector: 'app-diagnostics-dynamic-structure-component',
  templateUrl: './diagnostics-dynamic-structure-component.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsDynamicStructureComponentComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dynamicStructureComponentContainer', { read: ViewContainerRef })
  structureContainer: ViewContainerRef;
  @Input() diagnosticsFormGroupService: DiagnosticsFormGroupService;
  @Input() isVddEditable: boolean;
  @Input() parameterId: number;
  @Input() logicalLink: string;
  @Input() vddName: string;

  setNewValueEventEmitter: EventEmitter<SetNewValueObject> = new EventEmitter<SetNewValueObject>();
  setRawSingleReadValuesEventEmitter: EventEmitter<{
    rawSingleReadValues: RemoteDiagnosticsSuccessResponseData;
  }> = new EventEmitter<{ rawSingleReadValues: RemoteDiagnosticsSuccessResponseData }>();
  skipIndexes: number[] = [];
  flattenedStructure: RemoteDiagnosticsVddGenericStructure[];
  flattenedMetadata: RemoteDiagnosticsVddGenericStructure[];
  rawSingleReadValues: RemoteDiagnosticsSuccessResponseData;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private diagnosticsWebsocketService: DiagnosticsWebsocketService<
      RemoteDiagnosticsSuccessResponseData
    >,
    private vddDetailsListStore: VddDetailsListStore,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.vddDetailsListStore
      .getMatchedValues(this.vddName, this.logicalLink)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((values) => !!values.matchedValues && values.matchedValues.length > 0),
        distinctUntilChanged(isEqual)
      )
      .subscribe((values) => {
        this.structureContainer.clear();

        this.flattenedStructure = values.flattenedStructure;
        this.rawSingleReadValues = values.rawSingleRead;
        this.flattenedMetadata = values.flattenedMetadata;

        values.matchedValues.forEach((currentValue, index, allValues) => {
          this.createVddStructureTemplate(currentValue, values.rawSingleRead, index, allValues);
        });
        this.cdr.markForCheck();
      });

    this.diagnosticsWebsocketService.webSocketValues
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((webSocket) => this.vddName === webSocket.vdd),
        filter((value) => !!value?.data)
      )
      .subscribe((value) => {
        this.structureContainer.clear();

        const rawSingleReadValues = value?.data;
        this.rawSingleReadValues = rawSingleReadValues;
        const matchedValues = VddDetailsListHelper.getMatchValues(
          this.flattenedStructure,
          VddDetailsListHelper.getFlattenedSingleRead(value?.data)
        );
        matchedValues.forEach((currentValue, index, allValues) => {
          this.createVddStructureTemplate(currentValue, rawSingleReadValues, index, allValues);
        });
        this.cdr.detectChanges();
      });

    this.setNewValueEventEmitter
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(({ path, newValue }) => {
        this.vddDetailsListStore.setNewValue(this.vddName, this.logicalLink, path, newValue);
      });

    this.setRawSingleReadValuesEventEmitter
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(({ rawSingleReadValues }) => {
        this.vddDetailsListStore.setRawSingleReadValues(
          this.vddName,
          this.logicalLink,
          rawSingleReadValues
        );
      });
  }

  private createVddStructureTemplate(
    structure: RemoteDiagnosticsVddGenericStructure,
    rawSingleRead: RemoteDiagnosticsSuccessResponseData,
    index: number,
    allMatchedValues: RemoteDiagnosticsVddGenericStructure[]
  ): void {
    if (
      structure?.semantic === SemanticValues.RESERVED ||
      structure?.semantic === SemanticValues.ENTRIES ||
      structure.skip
    ) {
      return null;
    } else if (structure?.isSensor) {
      createSensorComponent(this.structureContainer, structure);
    } else if (structure?.semantic === SemanticValues.STARTSTOPTEMP) {
      createVddStartStopComponent(
        this.structureContainer,
        rawSingleRead,
        this.isVddEditable,
        this.parameterId,
        allMatchedValues,
        this.setRawSingleReadValuesEventEmitter
      );
    } else if (
      structure?.structure?.length === 6 &&
      structure?.type === TypeValues.eTABLE &&
      structure.semantic === ''
    ) {
      createDiagnosticsVinComponent(
        this.structureContainer,
        this.isVddEditable,
        this.diagnosticsFormGroupService,
        this.setNewValueEventEmitter,
        structure,
        this.vddName
      );
    } else if (
      structure?.semantic !== SemanticValues.DESCRIPTION &&
      DiagnosticsHelpers.equalsElementsInKey(structure, DataKeyValues.TranslationId, [
        TranslationIdValues.Day,
        TranslationIdValues.Month,
        TranslationIdValues.Year,
      ])
    ) {
      createDiagnosticsVddDateComponent(
        this.structureContainer,
        this.isVddEditable,
        this.skipIndexes,
        index,
        allMatchedValues,
        this.setNewValueEventEmitter
      );
    } else if (DiagnosticsHelpers.isStructureIpAddressType(structure)) {
      createDiagnosticsVddIpAddressComponent(
        this.structureContainer,
        this.isVddEditable,
        index,
        allMatchedValues,
        this.setNewValueEventEmitter
      );
    } else if (DiagnosticsHelpers.isStructureMacAddressType(structure)) {
      createDiagnosticsVddMacAddressComponent(
        this.structureContainer,
        this.isVddEditable,
        index,
        allMatchedValues,
        this.setNewValueEventEmitter
      );
    } else if (
      DiagnosticsHelpers.includesElementsInKey(structure, DataKeyValues.Type, [
        TypeValues.INT32,
        TypeValues.FLOAT32,
      ])
    ) {
      createDiagnosticsVddNumberComponent(
        this.structureContainer,
        this.isVddEditable,
        this.diagnosticsFormGroupService,
        allMatchedValues,
        this.flattenedMetadata,
        index,
        this.setNewValueEventEmitter
      );
    } else if (
      DiagnosticsHelpers.includesElementsInKey(structure, DataKeyValues.Semantic, [
        SemanticValues.BOOLEANCHOICE,
      ])
    ) {
      createVddBooleanChoiceComponent(
        this.structureContainer,
        this.isVddEditable,
        structure,
        this.setNewValueEventEmitter
      );
    } else if (
      DiagnosticsHelpers.includesElementsInKey(structure, DataKeyValues.Semantic, [
        SemanticValues.ENUM,
        SemanticValues.ENUMCHOICE,
        SemanticValues.BOOLEAN,
        SemanticValues.ACTUATORVALUE,
      ]) ||
      DiagnosticsHelpers.includesElementsInKey(structure, DataKeyValues.Longname, [
        LongnameValues.WeekDay,
      ])
    ) {
      createDiagnosticsVddEnumComponent(
        this.structureContainer,
        this.isVddEditable,
        structure,
        this.setNewValueEventEmitter
      );
    } else {
      createDiagnosticsTextComponent(
        this.structureContainer,
        this.isVddEditable,
        structure,
        this.setNewValueEventEmitter
      );
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
